import React from 'react';
import { CheckCircle, Calendar, User, Mail, Phone, FileText, Home, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ConfirmationStep({ 
  agent, 
  appointmentDate, 
  appointmentTime, 
  purpose, 
  clientInfo,
  onReset 
}) {
  const navigate = useNavigate();

  // CRITICAL FIX: Extract agent properties with fallback for both snake_case and camelCase
  const agentFullName = agent?.full_name || agent?.fullName || 'Team Member';
  const agentEmail = agent?.email || '';
  const agentPhone = agent?.phone || '';
  const agentYearsExp = agent?.years_experience || agent?.yearsExperience || null;
  const agentTitle = agent?.title || 'Bail Agent';

  const formatTime = (time) => {
    const [hours] = time?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${ampm}`;
  };

  const purposeMap = {
    'initial_consultation': 'Initial Consultation',
    'case_review': 'Case Review',
    'payment_discussion': 'Payment Discussion',
    'document_signing': 'Document Signing',
    'emergency_meeting': 'Emergency Meeting'
  };

  return (
    <div className="text-center">
      {/* Success Icon */}
      <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
        <CheckCircle className="w-12 h-12 text-green-600" />
      </div>
      {/* Success Message */}
      <h2 className="text-3xl font-bold text-gray-900 mb-4">
        Appointment Request Submitted!
      </h2>
      <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
        Your appointment request has been sent to <strong>{agentFullName}</strong> for approval. 
        You will receive an email confirmation once your appointment is approved.
      </p>
      {/* Appointment Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 mb-8 text-left max-w-2xl mx-auto border border-blue-200">
        <h3 className="text-xl font-semibold text-blue-900 mb-6 text-center">
          Appointment Summary
        </h3>

        <div className="space-y-4">
          {/* Agent - ENHANCED with full details */}
          <div className="flex items-start gap-4 bg-white p-5 rounded-lg shadow-sm">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {agentFullName?.split(' ')?.map(n => n?.[0])?.join('') || '?'}
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-1">Meeting with</p>
              <p className="font-bold text-gray-900 text-lg">{agentFullName}</p>
              <p className="text-sm text-blue-700 font-medium mb-2">{agentTitle}</p>
              
              {/* Agent contact details */}
              <div className="space-y-1.5 mt-3 pt-3 border-t border-gray-200">
                {agentYearsExp && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Award className="w-4 h-4 text-blue-600" />
                    <span>{agentYearsExp} years experience</span>
                  </div>
                )}
                {agentEmail && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Mail className="w-4 h-4 text-blue-600" />
                    <a href={`mailto:${agentEmail}`} className="hover:text-blue-600 transition-colors">
                      {agentEmail}
                    </a>
                  </div>
                )}
                {agentPhone && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Phone className="w-4 h-4 text-blue-600" />
                    <a href={`tel:${agentPhone}`} className="hover:text-blue-600 transition-colors">
                      {agentPhone}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex items-start gap-4 bg-white p-4 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <p className="text-sm text-gray-600">Date & Time</p>
              <p className="font-semibold text-gray-900">
                {appointmentDate?.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
              <p className="text-sm text-gray-600">{formatTime(appointmentTime)} PT</p>
            </div>
          </div>

          {/* Purpose */}
          <div className="flex items-start gap-4 bg-white p-4 rounded-lg">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <p className="text-sm text-gray-600">Meeting Purpose</p>
              <p className="font-semibold text-gray-900">{purposeMap?.[purpose]}</p>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white p-4 rounded-lg space-y-3">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-gray-500" />
              <span className="text-sm text-gray-900">{clientInfo?.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-500" />
              <span className="text-sm text-gray-900">{clientInfo?.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-gray-500" />
              <span className="text-sm text-gray-900">{clientInfo?.phone}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Next Steps */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8 max-w-2xl mx-auto">
        <h4 className="font-semibold text-yellow-900 mb-3 text-lg">📧 What happens next?</h4>
        <ul className="text-sm text-yellow-800 space-y-2 text-left">
          <li className="flex items-start gap-2">
            <span className="text-yellow-600 font-bold">1.</span>
            <span>{agentFullName} will review your appointment request</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-yellow-600 font-bold">2.</span>
            <span>You'll receive an email confirmation when approved</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-yellow-600 font-bold">3.</span>
            <span>If there are any conflicts, we'll contact you to reschedule</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-yellow-600 font-bold">4.</span>
            <span>Check your email for appointment details and reminders</span>
          </li>
        </ul>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Home className="w-5 h-5" />
          <span>Return Home</span>
        </button>
        <button
          onClick={onReset}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
        >
          Schedule Another Appointment
        </button>
      </div>
      {/* Emergency Contact */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg max-w-2xl mx-auto">
        <p className="text-sm text-gray-600">
          <strong>Need immediate assistance?</strong> Call us 24/7 at{' '}
          <a href="tel:+19162563468" className="text-blue-600 font-semibold hover:text-blue-700">
            (916) 256-3468
          </a>
        </p>
      </div>
    </div>
  );
}
